import dayjs from 'dayjs'
import { CSVLink } from 'react-csv'
import styled from 'styled-components/macro'
import useStockRoleCapabilities from 'src/hooks/useStockRoleCapabilities'
import { WASTAGE_COPY } from 'src/constants/wastage'
import {
  stockInStockWasEntered,
  stockSoldWasEntered,
  wasteExceedsStockOnHand,
} from 'src/utils/stockOnHand'

const StockTakeCSV = ({
  stockTake,
  stockTakeProducts,
  asMenuItem = false,
  linkRef,
}) => {
  const { canViewExpectedAndVariance: canViewSensitiveColumns } =
    useStockRoleCapabilities()
  // Helper function to format numbers with 2-3 decimals and remove trailing zeros
  const formatDecimal = (value, maxDecimals = 3) => {
    if (
      value === null ||
      value === undefined ||
      isNaN(value) ||
      !isFinite(value)
    ) {
      return 0
    }
    const num = Number(value)
    // Round to maxDecimals to handle floating-point errors
    const rounded =
      Math.round(num * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals)
    // Convert to string and remove trailing zeros
    const str = rounded.toString()
    // If it's a whole number, return as-is
    if (str.indexOf('.') === -1) {
      return rounded
    }
    // Remove trailing zeros and trailing decimal point
    return parseFloat(str)
  }

  // Helper function to calculate expected quantity
  const calculateExpectedQty = product => {
    try {
      if (!product) return 0
      const lastQty = Number(product.lastQuantity) || 0
      const received = Number(product.qtySinceLastStockTake) || 0
      const sold = Number(product.sold) || 0
      const wastage = Number(product.wastage) || 0

      const result = lastQty + received - sold - wastage

      // Handle invalid calculations
      if (isNaN(result) || !isFinite(result)) {
        console.warn('Invalid expected quantity calculation for CSV:', product)
        return 0
      }

      return result
    } catch (error) {
      console.error(
        'Error calculating expected quantity for CSV:',
        error,
        product,
      )
      return 0
    }
  }

  // Helper function to calculate variance
  const calculateVariance = product => {
    try {
      if (!product) {
        return { unitVariance: 0, percentVariance: 0, expectedQty: 0 }
      }

      const expectedQty = calculateExpectedQty(product)
      const inStock = Number(product.inStock) || 0

      // Handle invalid inputs
      if (isNaN(inStock) || !isFinite(inStock)) {
        console.warn('Invalid inStock value for CSV:', product)
        return { unitVariance: 0, percentVariance: 0, expectedQty }
      }

      const unitVariance = inStock - expectedQty
      let percentVariance = 0

      if (expectedQty !== 0 && isFinite(expectedQty)) {
        percentVariance = (unitVariance / expectedQty) * 100
        // Handle invalid percentage
        if (isNaN(percentVariance) || !isFinite(percentVariance)) {
          percentVariance = 0
        }
      }

      return { unitVariance, percentVariance, expectedQty }
    } catch (error) {
      console.error('Error calculating variance for CSV:', error, product)
      return { unitVariance: 0, percentVariance: 0, expectedQty: 0 }
    }
  }

  const escapeCsvField = value => {
    if (value === undefined || value === null) return ''
    // Preserve content; escape inner quotes per RFC by doubling them
    return String(value).replace(/"/g, '""')
  }
  const wrapCsvField = value => `"${escapeCsvField(value)}"`
  const rangeInString = `${dayjs(stockTake?.fromPeriod).format(
    'YYYY-MM-DD',
  )} - ${dayjs(stockTake?.toPeriod).format('YYYY-MM-DD')}`
  const baseHeaders = [
    'id',
    'Title',
    'Type',
    'Size',
    'UoM',
    'Supplier',
    'Price',
    'SKU',
    'Prev quantity',
    'Received',
    'Sold',
    'In stock',
    'Wastage',
    'Wastage comment',
  ]
  const sensitiveHeaders = ['Expected qty', 'Variance']
  const tableHeaders = canViewSensitiveColumns
    ? [
        ...baseHeaders.slice(0, 12),
        ...sensitiveHeaders,
        ...baseHeaders.slice(12),
      ]
    : baseHeaders

  const titleOfTable = `Stock take for ${rangeInString}`
  const rows = []
  rows.push([titleOfTable])
  rows.push(['Count type', stockTake?.countType || 'MAIN'])
  rows.push([
    'Site name',
    stockTake?.siteName && String(stockTake.siteName).trim()
      ? stockTake.siteName
      : 'No details provided',
  ])
  rows.push([
    'Counted by',
    stockTake?.countedByName && String(stockTake.countedByName).trim()
      ? stockTake.countedByName
      : 'No details provided',
  ])
  rows.push([
    'Reason for count',
    stockTake?.reasonForCount && String(stockTake.reasonForCount).trim()
      ? stockTake.reasonForCount
      : 'No details provided',
  ])
  rows.push([
    'Count notes',
    stockTake?.countNotes && String(stockTake.countNotes).trim()
      ? stockTake.countNotes
      : 'No details provided',
  ])
  rows.push([])
  rows.push(tableHeaders)

  stockTakeProducts?.forEach(product => {
    try {
      if (!product) return

      const {
        _id: id,
        title,
        type,
        size,
        UoM,
        supplierTitle,
        price,
        sku,
        lastQuantity,
        qtySinceLastStockTake,
        sold,
        inStock,
        wastage,
        wastageComment,
      } = product

      // Calculate expected quantity (format to match table display)
      const overWaste = wasteExceedsStockOnHand(product)
      const expectedQty = calculateExpectedQty(product)
      const formattedExpectedQty = overWaste
        ? WASTAGE_COPY.wasteBiggerThanOnHand
        : formatDecimal(expectedQty)

      // Calculate variance (format to match table display)
      const { unitVariance, percentVariance } = calculateVariance(product)
      const safeUnitVariance =
        isNaN(unitVariance) || !isFinite(unitVariance)
          ? 0
          : formatDecimal(unitVariance)
      const safePercentVariance =
        isNaN(percentVariance) || !isFinite(percentVariance)
          ? 0
          : formatDecimal(percentVariance)
      const counted = stockInStockWasEntered(product)
      const formattedVariance = !counted
        ? ''
        : overWaste
        ? WASTAGE_COPY.wasteBiggerThanOnHand
        : `${safeUnitVariance} (${safePercentVariance}%)`
      const formattedInStock = counted ? formatDecimal(inStock || 0) : ''

      // Format sold value
      const formattedSold = stockSoldWasEntered(product)
        ? formatDecimal(sold || 0)
        : ''

      const baseRow = [
        id || '',
        title || '',
        type || '',
        size || '',
        UoM || '',
        supplierTitle || '',
        price || 0,
        sku || '',
        lastQuantity || 0,
        qtySinceLastStockTake || 0,
        formattedSold,
        formattedInStock,
        wastage || 0,
        wastageComment && String(wastageComment).trim()
          ? wastageComment
          : 'No details provided',
      ]

      const row = canViewSensitiveColumns
        ? [
            ...baseRow.slice(0, 12),
            formattedExpectedQty,
            formattedVariance,
            ...baseRow.slice(12),
          ]
        : baseRow

      rows.push(row)
    } catch (error) {
      console.error('Error processing product for CSV:', error, product)
      // Skip invalid products but continue processing others
    }
  })

  const csvString = rows.map(row => row.map(wrapCsvField).join(',')).join('\n')

  if (asMenuItem) {
    return (
      <CSVLink
        ref={linkRef}
        data={csvString}
        filename={`Stock_take_${rangeInString}.csv`}
        aria-hidden='true'
        tabIndex={-1}
        style={{ display: 'none' }}
      />
    )
  }

  return (
    <DownloadCsvLink
      data={csvString}
      filename={`Stock_take_${rangeInString}.csv`}
    >
      Download CSV
    </DownloadCsvLink>
  )
}

const DownloadCsvLink = styled(CSVLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  box-sizing: border-box;
  padding: 0 14px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  background: #fff;
  color: inherit;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
`

export default StockTakeCSV
